/**
 * EINE Unterhaltung je Meldung (Dach #43, Scheibe 1): Erfassungs-Dialog,
 * Meldungs-Chat und Verlauf verschmelzen zu einem chronologischen Strang.
 *
 * Drei Zeilenarten:
 *  - Nachrichten (Melder/Entwicklung) als Blasen — beschriftet aus der
 *    Perspektive des Betrachters („an die Entwicklung" / „an den Melder")
 *  - Ereignisse (Statuswechsel, Übergabe ans Issue, Abschluss) als schmale
 *    Zwischenzeilen, damit der Takt sichtbar bleibt ohne den Lesefluss zu
 *    stören
 *  - der Erfassungs-Dialog als eingeklappter Anfang des Strangs
 *
 * Die Sektion ist zuklappbar; zugeklappt zeigt die Kopfzeile, wie viele
 * Nachrichten seit dem letzten Ansehen dazukamen (localStorage-Lesestand
 * wie bisher, F39 — der serverseitige Lesestand kommt mit Scheibe 2).
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronRight, Lock, Send, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { STATUS_LABEL } from "@/lib/labels"
import { api, apiError } from "@/services/api"
import type {
  MeldungKommentar,
  ReportDetail,
  RueckfrageEntwurf,
  StatusHistoryEintrag,
} from "@/types"

/** Ereigniszeile aus einem Statuswechsel — erzählt, nicht protokolliert. */
function ereignisText(v: StatusHistoryEintrag, report: ReportDetail): string {
  const b = v.begruendung ?? ""
  if (v.vonStatus === "entwurf" && v.nachStatus === "neu") return "Meldung abgesendet"
  if (v.nachStatus === "gesichtet") return "Von der Entwicklung gesichtet"
  const issue = /Forgejo-Issue #(\d+)/.exec(b)
  if (issue && v.nachStatus === "in_umsetzung" && !b.includes("reopened")) {
    return `An die Entwicklung übergeben (Issue #${issue[1]})`
  }
  if (b.includes("closed")) return "Die Entwicklung hat die Umsetzung abgeschlossen — erledigt"
  if (b.includes("reopened")) return "Wieder geöffnet — die Meldung ist zurück bei der Entwicklung"
  const wechsel = `${v.vonStatus ? `${STATUS_LABEL[v.vonStatus]} → ` : ""}${STATUS_LABEL[v.nachStatus]}`
  void report
  return b ? `${wechsel} (${b})` : wechsel
}

/** Ein Strang-Element: Nachricht oder Ereignis, gemeinsam sortierbar. */
type StrangEintrag =
  | { art: "nachricht"; zeit: string; nachricht: MeldungKommentar }
  | { art: "ereignis"; zeit: string; text: string }

interface Props {
  report: ReportDetail
  istReviewer: boolean
}

export function Unterhaltung({ report, istReviewer }: Props) {
  const queryClient = useQueryClient()
  const [offen, setOffen] = useState(true)
  const [erfassungOffen, setErfassungOffen] = useState(false)
  const [text, setText] = useState("")
  const endeRef = useRef<HTMLDivElement>(null)

  const { data: kommentare } = useQuery({
    queryKey: ["reports", report.id, "kommentare"],
    queryFn: () => api.reports.kommentare.list(report.id),
    refetchInterval: 30_000,
  })

  // Scheibe 2 (#43): Lesestand SERVERSEITIG — gesehen wird nur markiert,
  // wenn der Strang offen ist; zugeklappt soll der Zähler ja stehen bleiben.
  // `gelesenBis` hält die Antwort des Servers, damit der zugeklappte Zähler
  // auch nach dem Öffnen und trotz Poll-Nachschub stimmt.
  const [gelesenBis, setGelesenBis] = useState<number | null>(null)
  const gelesen = useMutation({
    mutationFn: () => api.reports.kommentare.gelesen(report.id),
    onSuccess: (r) => setGelesenBis(r.gelesenBis),
  })
  useEffect(() => {
    if (offen && kommentare) {
      gelesen.mutate()
      endeRef.current?.scrollIntoView({ block: "nearest" })
    }
    // gelesen bewusst nicht in den Abhängigkeiten: die Mutation-Identität
    // wechselt je Render, ausschlaggebend sind Öffnung + Nachrichtenstand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, kommentare, report.id])

  // Vor der ersten Markierung zählt der Server-Wert aus dem Report-Laden,
  // danach alles oberhalb der zuletzt bestätigten Kommentar-ID.
  const neue =
    gelesenBis === null
      ? report.ungeleseneKommentare
      : (kommentare ?? []).filter((k) => k.id > gelesenBis).length

  const strang = useMemo<StrangEintrag[]>(() => {
    const eintraege: StrangEintrag[] = [
      ...report.verlauf.map((v) => ({
        art: "ereignis" as const,
        zeit: v.zeitpunkt,
        text: ereignisText(v, report),
      })),
      ...(kommentare ?? []).map((k) => ({
        art: "nachricht" as const,
        zeit: k.erstelltAm,
        nachricht: k,
      })),
    ]
    return eintraege.sort((a, b) => new Date(a.zeit).getTime() - new Date(b.zeit).getTime())
  }, [report, kommentare])

  // Scheibe 3 (#34): der Reviewer wählt, ob seine Nachricht eine FRAGE ist —
  // nur die setzt beim Melder die Rückfrage-Pille. Kunden senden immer info.
  const [alsFrage, setAlsFrage] = useState(false)
  const senden = useMutation({
    mutationFn: () =>
      api.reports.kommentare.create(
        report.id,
        text.trim(),
        istReviewer && alsFrage ? "frage" : "info"
      ),
    onSuccess: () => {
      setText("")
      setAlsFrage(false)
      void queryClient.invalidateQueries({ queryKey: ["reports", report.id, "kommentare"] })
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const abschicken = () => {
    if (text.trim() && !senden.isPending) senden.mutate()
  }

  // Scheibe 3: KI-Hilfe — Reviewer formuliert Rückfragen (mit Erstbeantworter),
  // Melder bekommt Hilfe beim Antworten. Beides landet als ENTWURF im
  // Eingabefeld; gesendet wird erst nach menschlichem Blick.
  const [kiOffen, setKiOffen] = useState(false)
  const [stichworte, setStichworte] = useState("")
  const [entwurf, setEntwurf] = useState<RueckfrageEntwurf | null>(null)
  const frageEntwerfen = useMutation({
    mutationFn: () => api.reports.rueckfrageEntwurf(report.id, stichworte.trim()),
    onSuccess: (e) => {
      setEntwurf(e)
      if (!e.schonBeantwortet && e.frageEntwurf) {
        setText(e.frageEntwurf)
        setAlsFrage(true)
      }
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const antwortHelfen = useMutation({
    mutationFn: () => api.reports.antwortHilfe(report.id, stichworte.trim()),
    onSuccess: (r) => {
      setText(r.antwort)
      setKiOffen(false)
      setStichworte("")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // Perspektive des Betrachters (Dach #43): die eigene Seite steht rechts,
  // die Beschriftung nennt die Richtung — nicht die abstrakte Rolle.
  const eigeneSeite = istReviewer ? "entwicklung" : "kunde"
  const kopfzeile = (k: MeldungKommentar): string => {
    const name = k.autorName || (k.rolle === "entwicklung" ? "Entwicklung" : "Melder")
    if (k.intern) return `${name} · intern (Forgejo)`
    // Scheibe 6 (#43): Kunden-Nachricht eines Dritten kennzeichnen
    const dritter = k.rolle === "kunde" && !k.vomMelder ? " (ebenfalls betroffen)" : ""
    if (k.rolle === eigeneSeite) {
      return `${name}${dritter} · an ${istReviewer ? "den Melder" : "die Entwicklung"}`
    }
    return istReviewer
      ? `${name} (${k.vomMelder ? "Melder" : "ebenfalls betroffen"})`
      : `${name} (Entwicklung) · an dich`
  }

  return (
    <section className="rounded-xl border bg-card p-5 shadow-card">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className="flex w-full items-center gap-2 text-left"
      >
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform ${offen ? "rotate-90" : ""}`}
        />
        <h2 className="text-[13px] font-bold uppercase tracking-wide text-foreground/70">
          Unterhaltung
          {kommentare && kommentare.length > 0 && (
            <span className="ml-1.5 font-semibold text-muted-foreground">
              · {kommentare.length} Nachricht{kommentare.length === 1 ? "" : "en"}
            </span>
          )}
        </h2>
        {!offen && neue > 0 && (
          <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-primary-foreground">
            {neue} neue
          </span>
        )}
        {report.rueckfrageOffen && !istReviewer && (
          <span
            title="Die Entwicklung hat eine Rückfrage gestellt, die noch unbeantwortet ist"
            className="rounded-full bg-warning px-2.5 py-0.5 text-xs font-bold text-warning-foreground"
          >
            Rückfrage offen — bitte antworten
          </span>
        )}
      </button>

      {offen && (
        <div className="mt-3 flex flex-col gap-3">
          {/* Farb-Legende: die Rollenfarben erklären sich selbst */}
          <div className="flex items-center gap-4 text-[11.5px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full border border-amber-400/40 bg-amber-400/30" />
              Melder
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full border border-sky-400/40 bg-sky-400/30" />
              Entwicklung
            </span>
            {istReviewer && (
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full border border-dashed border-sky-400/50" />
                intern (nur Reviewer)
              </span>
            )}
          </div>

          {/* Der Anfang des Strangs: Erfassungs-Dialog, eingeklappt */}
          {report.chat.length > 0 && (
            <div className="rounded-[10px] border">
              <button
                type="button"
                onClick={() => setErfassungOffen((o) => !o)}
                className="flex w-full items-center gap-2 px-3.5 py-2 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 transition-transform ${erfassungOffen ? "rotate-90" : ""}`}
                />
                Erfassungs-Dialog · {report.chat.length} Nachrichten
              </button>
              {erfassungOffen && (
                <div className="flex flex-col gap-2 border-t p-3.5">
                  {report.chat.map((n) => (
                    <div
                      key={n.id}
                      className={`max-w-[75%] whitespace-pre-wrap rounded-[10px] px-3 py-2 text-[13px] ${
                        n.rolle === "kunde"
                          ? "self-end border border-amber-400/25 bg-amber-400/10"
                          : "self-start bg-muted"
                      }`}
                    >
                      {n.inhalt}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Der Strang: Nachrichten + Ereignisse, chronologisch */}
          <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto pr-1">
            {strang.map((e, i) =>
              e.art === "ereignis" ? (
                <div key={`e${i}`} className="flex items-center gap-2 py-0.5">
                  <span className="h-px flex-1 bg-border" />
                  <span className="shrink-0 text-[11.5px] font-medium text-muted-foreground">
                    {e.text} ·{" "}
                    {new Date(e.zeit).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              ) : e.nachricht.autorName === "System" ? (
                // Scheibe 4: Systemnachrichten (PR eröffnet/gemergt) lesen
                // sich wie Ereignisse, nicht wie Gesprächsbeiträge
                <div key={`n${e.nachricht.id}`} className="flex items-center gap-2 py-0.5">
                  <span className="h-px flex-1 bg-border" />
                  <span className="shrink-0 text-[11.5px] font-medium text-muted-foreground">
                    {e.nachricht.text} ·{" "}
                    {new Date(e.nachricht.erstelltAm).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              ) : (
                <div
                  key={`n${e.nachricht.id}`}
                  className={`max-w-[85%] rounded-[10px] px-3.5 py-2.5 text-sm ${
                    // Farbe nach ROLLE (Wunsch 12.08.2026): Melder blassgelb,
                    // Entwicklung hellblau — auf beiden Seiten gleich, damit
                    // Melder und Reviewer dieselben Farben sehen. Die eigene
                    // Seite steht weiterhin rechts. Transluzente Tönungen,
                    // damit es im dunklen Thema trägt.
                    e.nachricht.intern
                      ? "self-start border border-dashed border-sky-400/30 bg-sky-400/5 opacity-90"
                      : `${
                          e.nachricht.rolle === eigeneSeite ? "self-end" : "self-start"
                        } border ${
                          e.nachricht.rolle === "kunde"
                            ? "border-amber-400/25 bg-amber-400/10"
                            : "border-sky-400/25 bg-sky-400/10"
                        }`
                  }`}
                >
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {kopfzeile(e.nachricht)} ·{" "}
                    {new Date(e.nachricht.erstelltAm).toLocaleString("de-DE")}
                    {e.nachricht.art === "frage" && (
                      <span className="rounded-full bg-warning px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-warning-foreground">
                        Frage
                      </span>
                    )}
                    {e.nachricht.intern && (
                      <Lock className="h-3 w-3" aria-label="Nur für Reviewer sichtbar" />
                    )}
                  </p>
                  <p className="whitespace-pre-wrap leading-relaxed">{e.nachricht.text}</p>
                </div>
              )
            )}
            {strang.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Noch keine Einträge — Fragen zur Meldung kannst du hier direkt stellen.
              </p>
            )}
            <div ref={endeRef} />
          </div>

          {/* Scheibe 3: KI-Hilfe — Entwurf entsteht hier, gesendet wird unten */}
          {report.status !== "entwurf" && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setKiOffen((o) => !o)}
                className="flex items-center gap-1.5 self-start rounded-lg px-2 py-1 text-[12px] font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {istReviewer
                  ? "Rückfrage mit KI formulieren"
                  : "Antwort mit KI-Hilfe formulieren"}
              </button>
              {kiOffen && (
                <div className="flex flex-col gap-2 rounded-[10px] border border-primary/20 bg-primary/5 p-3">
                  <textarea
                    value={stichworte}
                    onChange={(e) => setStichworte(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder={
                      istReviewer
                        ? "Rohe Stichworte — die KI prüft erst die Unterlagen und formuliert dann kundentauglich …"
                        : "Rohe Stichworte — die KI formuliert daraus deine Antwort …"
                    }
                    className="rounded-lg border bg-background px-3 py-2 text-[13px]"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        istReviewer ? frageEntwerfen.mutate() : antwortHelfen.mutate()
                      }
                      disabled={
                        !stichworte.trim() ||
                        frageEntwerfen.isPending ||
                        antwortHelfen.isPending
                      }
                      className="rounded-lg bg-primary px-3 py-1.5 text-[12px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                    >
                      {frageEntwerfen.isPending || antwortHelfen.isPending
                        ? "KI arbeitet …"
                        : "Formulieren lassen"}
                    </button>
                    <span className="text-[11.5px] text-muted-foreground">
                      Der Entwurf landet im Nachrichtenfeld — gesendet wird erst von dir.
                    </span>
                  </div>
                  {/* Erstbeantworter: die Unterlagen beantworten die Frage schon */}
                  {istReviewer && entwurf?.schonBeantwortet && (
                    <div className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-[13px]">
                      <p className="font-bold text-success">
                        Das beantworten die Unterlagen bereits — der Melder muss nicht
                        gefragt werden:
                      </p>
                      <p className="mt-1 whitespace-pre-wrap">
                        {entwurf.antwortAusUnterlagen}
                      </p>
                      {entwurf.hinweis && (
                        <p className="mt-1 text-muted-foreground">{entwurf.hinweis}</p>
                      )}
                    </div>
                  )}
                  {istReviewer && entwurf && !entwurf.schonBeantwortet && entwurf.hinweis && (
                    <p className="text-[12px] text-muted-foreground">{entwurf.hinweis}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Eingabe — Richtung aus der Perspektive des Betrachters.
              Beim Entwurf zu: der Chat öffnet mit dem Absenden (409 im API). */}
          {report.status !== "entwurf" && (
          <div className="flex items-end gap-2">
            {istReviewer && (
              <div
                className="flex shrink-0 flex-col overflow-hidden rounded-lg border text-[11px] font-bold"
                title="Nur eine FRAGE setzt beim Melder die Rückfrage-Pille"
              >
                {(
                  [
                    [false, "Info"],
                    [true, "Frage"],
                  ] as const
                ).map(([wert, label]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setAlsFrage(wert)}
                    className={`px-2.5 py-[5px] ${
                      alsFrage === wert
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  abschicken()
                }
              }}
              rows={2}
              maxLength={4000}
              placeholder={
                istReviewer
                  ? alsFrage
                    ? "Rückfrage an den Melder — setzt die Pille … (Enter sendet)"
                    : "Nachricht an den Melder … (Enter sendet)"
                  : "Nachricht an die Entwicklung … (Enter sendet)"
              }
              className="min-h-[44px] flex-1 rounded-lg border px-3 py-2 text-[13px]"
            />
            <button
              type="button"
              onClick={abschicken}
              disabled={!text.trim() || senden.isPending}
              className="flex h-[44px] items-center gap-1.5 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              Senden
            </button>
          </div>
          )}
        </div>
      )}
    </section>
  )
}
